import { useEffect, useRef } from 'react';

import type { BuildingState } from '@/api/rest';
import {
  getBuildingState,
  getDashboardSnapshot,
  replayEvents,
  ApiError,
} from '@/api/rest';
import { DashboardStream } from '@/api/ws';
import { useDashboardStore } from '@/state/store';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { isDemoModeRequested, startDemo } from '@/demo/seedDemo';
import { AudioCueDispatcher } from '@/audio/AudioCueDispatcher';

/**
 * Build the explicit subscription subject list from a building
 * snapshot.  We list each subject the backend uses (``system``,
 * ``building``, ``leaderboard``, plus one ``pod:<id>`` per pod)
 * because the dashboard backend's ``subscribe`` handler explicitly
 * skips replay for wildcard ``"*"`` subjects:
 *
 *   for subj in subscription.subjects:
 *       if subj == "*":
 *           continue                 # ← no historical events!
 *       replay = await event_bus.read_since(subj, cursor=since_id, ...)
 *
 * Subscribing with the explicit list gets us the backlog for every
 * subject we care about on (re)connect.  Live forwarding still
 * matches every subject (backend's ``matches()`` understands ``*``),
 * but historical events for pod subjects only flow when we name them.
 */
function collectStreamSubjects(building: BuildingState): string[] {
  // ``news`` is the external producer subject — NewsIntelligence publishes
  // ``news.*`` events through PodPlatform's ``/api/v1/events/external/news``
  // endpoint; the backend fans them onto ``events:news`` Redis stream and
  // we receive them through the same WebSocket as every other subject.
  const subjects = ['system', 'building', 'leaderboard', 'news'];
  for (const floor of building.floors) {
    for (const pod of floor.pods) {
      subjects.push(`pod:${pod.pod_id}`);
    }
  }
  return subjects;
}

import { AtmosphereHud } from '@/components/AtmosphereHud';
import { AudioMuteToggle } from '@/components/AudioMuteToggle';
import { BuildingOverview } from '@/components/BuildingOverview';
import { BuildingPixiView } from '@/components/BuildingPixiView';
import { CalendarHud } from '@/components/CalendarHud';
import { ConnectionBadge } from '@/components/ConnectionBadge';
import { EventFeed } from '@/components/EventFeed';
import { MobileBottomBar } from '@/components/MobileBottomBar';
import { NewsTicker } from '@/components/NewsTicker';
import { FridayCelebrationOverlay } from '@/components/FridayCelebrationOverlay';
import { HelpLegend } from '@/components/HelpLegend';
import { PodRoomPixiView } from '@/components/PodRoomPixiView';
import { PodRoomView } from '@/components/PodRoomView';
import { RendererToggle } from '@/components/RendererToggle';
import { SessionBanner } from '@/components/SessionBanner';
import { TooltipDelegate } from '@/components/TooltipDelegate';

/**
 * Top-level component.  Owns the side-effect lifecycle (one bootstrap
 * fetch + one open WebSocket per page load) and lets every child read
 * state via the Zustand store.
 *
 * Layout adapts per breakpoint:
 *
 *   mobile  ─ stacked single column, sticky header collapses calendar
 *             pills into a one-row summary, event feed lives in a
 *             bottom drawer that scrolls inside the viewport
 *   tablet  ─ two columns (main + 280px feed)
 *   desktop ─ two columns (main + 360px feed) — original layout
 */
export function App() {
  const setBuilding = useDashboardStore((s) => s.setBuilding);
  const hydrateFromSnapshot = useDashboardStore((s) => s.hydrateFromSnapshot);
  const appendEvent = useDashboardStore((s) => s.appendEvent);
  const setConnection = useDashboardStore((s) => s.setConnection);
  const setError = useDashboardStore((s) => s.setError);
  const view = useDashboardStore((s) => s.view);
  const buildingRenderer = useDashboardStore((s) => s.buildingRenderer);
  const bp = useBreakpoint();
  const isMobile = bp === 'mobile';
  const isTablet = bp === 'tablet';

  // -- Bootstrap.
  //
  // First mount: fetch ``/dashboard/snapshot`` -- a single round-trip
  // hydration carrying not just the building summary but every
  // per-pod state slot (positions, PM decision, utility, agent
  // phrases, nav history, risk/discipline/centerbook/policy).
  // Without this the dashboard would have to reconstruct that state
  // by replaying the 200-event localStorage window through reducers,
  // and anything emitted before the window (positions from earlier
  // cycles, the latest PM decision, the utility breakdown) would be
  // simply gone after a reload.
  //
  // Subsequent refreshes (every 30s) fall back to ``/building/state``
  // because the snapshot endpoint also runs the building view query
  // and we want to keep the polling cost bounded.  Live event-stream
  // updates take over for per-pod state between snapshots.
  //
  // Demo mode (``?demo=1``) replaces both the REST bootstrap and the
  // WebSocket stream with a local scripted source so the dashboard
  // works offline as a showcase.
  useEffect(() => {
    if (isDemoModeRequested()) {
      const handle = startDemo(useDashboardStore.getState());
      return () => handle.stop();
    }
    let stopped = false;
    let hydratedFromSnapshot = false;

    const refresh = async () => {
      try {
        if (!hydratedFromSnapshot) {
          // First load: try the rich snapshot endpoint.  On 404 (older
          // backend) or any other failure fall through to the cheaper
          // building/state path so the dashboard still boots.
          try {
            const snapshot = await getDashboardSnapshot();
            if (!stopped) {
              hydrateFromSnapshot(snapshot);
              hydratedFromSnapshot = true;
              return;
            }
          } catch (snapshotErr) {
            if (
              snapshotErr instanceof ApiError &&
              (snapshotErr.status === 404 || snapshotErr.status === 405)
            ) {
              // Older backend without the endpoint -- log + fall through.
              console.warn(
                'Snapshot endpoint missing, falling back to /building/state',
              );
            } else {
              throw snapshotErr;
            }
          }
        }
        const state = await getBuildingState();
        if (!stopped) setBuilding(state);
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          setError('Auth failed -- check VITE_API_KEY in build env');
        } else {
          setError(err);
        }
      }
    };
    void refresh();
    const id = window.setInterval(refresh, 30_000);
    return () => {
      stopped = true;
      window.clearInterval(id);
    };
  }, [hydrateFromSnapshot, setBuilding, setError]);

  // -- Live event stream.
  //
  // We open the WS immediately with a wildcard subject so live events
  // start flowing on mount.  ``subscriptionRef`` tracks the explicit
  // subject set we last sent so we can re-subscribe (and trigger a
  // backend backlog replay) the first time ``/building/state`` lands,
  // and again whenever the pod roster actually changes.
  const streamRef = useRef<DashboardStream | null>(null);
  const lastSubjectsRef = useRef<string | null>(null);
  useEffect(() => {
    if (isDemoModeRequested()) return; // demo runs its own stream
    const stream = new DashboardStream({
      onEvent: appendEvent,
      onState: setConnection,
      onError: setError,
      // Gap-fill when the WS sends ``resync_required`` -- our cursor
      // has been trimmed from the Redis Stream, but the REST replay
      // endpoint still has the window.  Paginate up to 5 batches of
      // 500 events so a multi-hour outage can recover without
      // hammering the API.  Each event is fed back through ``onEvent``
      // (= ``appendEvent``) so reducers behave identically to the
      // live path.
      onResync: async (sinceId: string) => {
        let cursor = sinceId;
        for (let page = 0; page < 5; page++) {
          const result = await replayEvents({
            subject: 'system',
            since: cursor,
            limit: 500,
          });
          if (!result.events.length) break;
          for (const env of result.events) appendEvent(env);
          const last = result.events[result.events.length - 1];
          if (!last) break;
          cursor = last.id;
          if (result.events.length < 500) break;
        }
      },
    });
    streamRef.current = stream;
    // Initial subscribe: wildcard so live events flow even before the
    // bootstrap fetch lands.  As soon as ``/building/state`` is in the
    // store the second effect below re-subscribes with explicit
    // subjects -- backend replays the backlog only for the named
    // subjects, so wildcard alone misses every historical pod event.
    stream.subscribe({
      subjects: ['*'],
      types: ['*'],
      includeSceneHint: true,
      // Backend ships a pre-formatted human sentence per envelope
      // ("Risk Officer заморозил pod_moex_qwen_plus за hard breach VaR").
      // Cheap to ship and powers the event-feed ``narrative`` fallback
      // when no specialised renderer exists.
      includeNarrative: true,
    });
    return () => {
      stream.close();
      streamRef.current = null;
      lastSubjectsRef.current = null;
    };
  }, [appendEvent, setConnection, setError]);

  // Re-subscribe with explicit subjects as soon as the building
  // snapshot lands (and whenever its pod roster changes).  This is
  // what triggers backend's per-subject ``read_since`` replay -- the
  // wildcard subscription alone never gets the backlog.
  const building = useDashboardStore((s) => s.building);
  useEffect(() => {
    if (isDemoModeRequested()) return;
    const stream = streamRef.current;
    if (!stream || !building) return;
    const subjects = collectStreamSubjects(building);
    const fingerprint = subjects.slice().sort().join('|');
    if (fingerprint === lastSubjectsRef.current) return;
    lastSubjectsRef.current = fingerprint;
    stream.subscribe({
      subjects,
      types: ['*'],
      includeSceneHint: true,
      includeNarrative: true,
    });
  }, [building]);

  // -- Deep link: ``?pod=<id>`` jumps straight into the pod room view.
  //    Runs once on mount; takes the ``pod_moex_`` prefix as optional.
  const selectPod = useDashboardStore((s) => s.selectPod);
  useEffect(() => {
    try {
      const raw = new URLSearchParams(window.location.search).get('pod');
      if (!raw) return;
      const podId = raw.startsWith('pod_') ? raw : `pod_moex_${raw}`;
      selectPod(podId);
    } catch {
      /* no-op */
    }
  }, [selectPod]);

  // -- Audio cue dispatcher.  One instance for the page lifetime.
  //    Hooks into the same store-update path the Pixi scenes use:
  //    every appended envelope is fed in, the dispatcher synthesises a
  //    cue when ``scene_hint.audio_cue`` is set and the user has
  //    unmuted.  Rate-limited to avoid chime-spam on busy cycles.
  const audioRef = useRef<AudioCueDispatcher | null>(null);
  if (audioRef.current === null) {
    audioRef.current = new AudioCueDispatcher();
  }
  useEffect(() => {
    const dispatcher = audioRef.current;
    if (!dispatcher) return;
    dispatcher.setMuted(useDashboardStore.getState().audioMuted);
    const unsubMute = useDashboardStore.subscribe(
      (state, prev) => {
        if (state.audioMuted !== prev.audioMuted) {
          dispatcher.setMuted(state.audioMuted);
        }
      },
    );
    const unsubFeed = useDashboardStore.subscribe((state) => {
      dispatcher.ingest(state.feed[state.feed.length - 1]);
    });
    return () => {
      unsubMute();
      unsubFeed();
      dispatcher.destroy();
      audioRef.current = null;
    };
  }, []);

  const feedColWidth = isTablet ? '280px' : '360px';
  // ``minmax(0, 1fr)`` lets the canvas column shrink below its content's
  // intrinsic width.  Plain ``1fr`` defaults to ``minmax(auto, 1fr)``
  // which uses ``min-content`` as the floor -- that pushes the feed off
  // the right edge on tablets.
  //
  // On mobile we ditch the event-feed column entirely -- CenterBook
  // and the feed both move into the ``MobileBottomBar`` strip with
  // bottom-sheets on tap, giving the building canvas the full
  // viewport minus a thin strip at the bottom.
  const mainColumns = isMobile ? 'minmax(0, 1fr)' : `minmax(0, 1fr) ${feedColWidth}`;
  // Mobile: building gets the leftover space, bottom bar gets its
  // intrinsic height (~80-100 px).  ``minmax(0, 1fr)`` is critical:
  // plain ``1fr`` resolves to ``minmax(auto, 1fr)``, which keeps the
  // row at least as tall as the canvas' min-content height -- on a
  // real phone the Pixi canvas (resized to its host) can briefly
  // request more than the available height during the first paint,
  // pushing the auto bar row off-screen.  ``minmax(0, 1fr)`` lets
  // the row shrink so the bar always renders within the viewport.
  const mainRows = isMobile ? 'minmax(0, 1fr) auto' : '1fr';

  return (
    <div
      className="app-root"
      style={{
        display: 'grid',
        // Three explicit rows: header, optional news ticker, main.
        // Plain ``auto 1fr`` resolved to ``header(auto), ticker(1fr),
        // main(implicit-auto)`` once NewsTicker rendered a chip --
        // ticker would balloon to fill the screen and main fell back
        // to content-height (canvas overflow + bar offscreen).
        // ``minmax(0, 1fr)`` on main ensures the canvas row is the
        // flex one and shrinks below content size if needed.
        gridTemplateRows: 'auto auto minmax(0, 1fr)',
        overflow: 'hidden',
      }}
    >
      <header
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          rowGap: 8,
          columnGap: 12,
          padding: isMobile ? '8px 12px' : '12px 20px',
          borderBottom: '1px solid #21262d',
          background: '#161b22',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <strong style={{ fontSize: isMobile ? 15 : 18, letterSpacing: 0.5 }}>
            QuantFund
          </strong>
          {!isMobile ? (
            <span style={{ color: '#8b949e', fontSize: 13 }}>live dashboard</span>
          ) : null}
          {isDemoModeRequested() ? (
            <a
              href="/"
              aria-label="Demo mode -- click to exit"
              title="Scripted demo data -- not the live feed.  Click to exit demo."
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: 0.6,
                padding: '2px 6px',
                borderRadius: 4,
                background: '#1f6feb',
                color: 'white',
                textTransform: 'uppercase',
                textDecoration: 'none',
              }}
            >
              demo
            </a>
          ) : (
            <a
              href="/?demo=1"
              aria-label="Start demo mode"
              title="Preview the dashboard with a scripted event stream"
              style={{
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: 0.4,
                padding: '2px 6px',
                borderRadius: 4,
                background: 'transparent',
                color: '#8b949e',
                textTransform: 'uppercase',
                textDecoration: 'none',
                border: '1px solid #30363d',
              }}
            >
              try demo
            </a>
          )}
        </div>
        <CalendarHud />
        <div
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <AtmosphereHud />
          <RendererToggle />
          <AudioMuteToggle />
          <HelpLegend />
          <ConnectionBadge />
        </div>
      </header>
      <NewsTicker />
      <main
        style={{
          display: 'grid',
          gridTemplateColumns: mainColumns,
          gridTemplateRows: mainRows,
          gap: 0,
          minHeight: 0,
          position: 'relative',
        }}
      >
        <SessionBanner />
        {view.kind === 'pod_room' ? (
          buildingRenderer === 'pixi' ? (
            <PodRoomPixiView podId={view.podId} />
          ) : (
            <PodRoomView podId={view.podId} />
          )
        ) : buildingRenderer === 'pixi' ? (
          <BuildingPixiView />
        ) : (
          <BuildingOverview />
        )}
        {isMobile ? <MobileBottomBar /> : <EventFeed />}
      </main>
      <FridayCelebrationOverlay />
      <TooltipDelegate />
    </div>
  );
}
